import 'chartjs-plugin-style';

import {
  CategoryScale,
  Chart as ChartJS,
  Filler,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from 'chart.js';
import { classNames } from '../utils';
import { Chart } from './Chartjs';

import { useDateRange } from '../hooks';

const HoverLine = {
  id: 'hover-line',
  afterDatasetDraw(chart, args, plugins) {
    const { ctx, tooltip, chartArea, scales } = chart;
    if (tooltip?._active && tooltip?._active.length > 0 && tooltip?.dataPoints?.[0]) {
      const xCoord = scales.x?.getPixelForValue(tooltip.dataPoints[0].dataIndex);
      const yCoord = scales.y?.getPixelForValue(tooltip.dataPoints[0].parsed.y);
      ctx.save();
      ctx.beginPath();
      ctx.setLineDash([5, 5]);
      ctx.lineWidth = 3;
      ctx.strokeStyle = 'rgba(134, 194, 235, .9)';
      ctx.moveTo(xCoord, yCoord);
      ctx.lineTo(xCoord, chartArea.bottom);
      ctx.stroke();
      ctx.setLineDash([]);
    }
  },
};

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Filler, Tooltip, HoverLine);

type Datum = {
  date: string;
  franchiseName: string;
  backendID: string;
  clubID: string;
  activemembers: number;
  terminations: any;
  activations: any;
  membersDevelopment: {
    movement: number;
    davonOnline: number;
    activeMembers: number;
    cancellations: number;
    activatedMembers: number;
    cancelledMembers: number;
    thereofTestPhaseCancelled: number;
  };
};

export const LineChart = ({ data, className, options, sugMin = true }: any) => {
  if (!data) return null;

  const singlePoint = data.datasets?.[0]?.data?.length === 1;
  // // Prüfen ob nur ein Datenpunkt vorhanden ist und ggf. duplizieren
  // if (data.datasets?.[0]?.data?.length === 1) {
  //   const singlePoint = data.datasets[0].data[0];
  //   data.datasets[0].data = [singlePoint, singlePoint];
  //
  //   if (data.labels?.length === 1) {
  //     const label = data.labels[0];
  //     data.labels = [label, label];
  //   }
  // }

  const { interval } = useDateRange();

  let minHeight = null;

  if (sugMin && data.datasets?.[0]?.data) {
    const localData = [...data.datasets?.[0]?.data];
    minHeight = Math.max(localData?.sort((a, b) => a.y - b.y)?.[0]?.y * 0.85, 0);
  }

  const dataInjected = {
    ...data,
    datasets: data?.datasets.map((d) => ({
      ...d,
      barThickness: 2000,
    })),
  };

  return (
    <div
      className={classNames('w-full', className)}
      style={{
        aspectRatio: '16 / 8',
      }}
    >
      <Chart
        type={singlePoint ? 'bar' : 'line'}
        id={'line-chart' + minHeight}
        options={{
          responsive: true,
          aspectRatio: 16 / 8,

          // animation: false,
          plugins: {
            //@ts-ignore
            hoverLine: HoverLine,
            legend: {
              display: false,
            },
            tooltip: {
              enabled: true,
              borderColor: 'black',
              borderWidth: 1,
              caretSize: 0,
              backgroundColor: 'white',
              titleColor: 'black',
              bodyColor: 'black',
              mode: 'index',
              intersect: true,
            },
          },

          events: ['mousemove', 'mouseout', 'click', 'touchstart', 'touchmove'],
          scales: {
            y: {
              grid: {
                display: false,
              },
              ticks: {
                precision: 0,
              },
              // 15% less than the lowest value
              suggestedMin: minHeight,
            },
            x: {
              grid: {
                display: false,
              },

              ticks: {
                callback: function (val, index) {
                  return interval === 'year'
                    ? index % 2 === 0
                      ? this.getLabelForValue(val)
                      : ''
                    : this.getLabelForValue(val);
                },
              },
            },
          },
          elements: {
            bar: {
              cubicInterpolationMode: 'monotone',
              borderWidth: 0,
              borderColor: (context) => {
                if (!context.chart.chartArea) return;
                const {
                  ctx,
                  chartArea: { left, right },
                } = context.chart;
                const gradientBg = ctx.createLinearGradient(left, 0, right, 0);
                gradientBg.addColorStop(0, 'rgba(134, 194, 235, 0)');
                gradientBg.addColorStop(1, 'rgba(134, 194, 235, .9)');
                return gradientBg;
              },
              backgroundColor: (context) => {
                if (!context.chart.chartArea) return;
                const {
                  ctx,
                  chartArea: { top, bottom },
                } = context.chart;
                const gradientBg = ctx.createLinearGradient(0, top, 0, bottom);
                gradientBg.addColorStop(0, 'rgba(134, 194, 235, .4)');
                gradientBg.addColorStop(1, 'rgba(134, 194, 235, 0)');
                return gradientBg;
              },
              fill: true,
            },

            line: {
              cubicInterpolationMode: 'monotone',
              borderWidth: 2,
              borderColor: (context) => {
                if (!context.chart.chartArea) return;
                const {
                  ctx,
                  chartArea: { left, right },
                } = context.chart;
                const gradientBg = ctx.createLinearGradient(left, 0, right, 0);
                gradientBg.addColorStop(0, 'rgba(134, 194, 235, 0)');
                gradientBg.addColorStop(1, 'rgba(134, 194, 235, .9)');
                return gradientBg;
              },
              backgroundColor: (context) => {
                if (!context.chart.chartArea) return;
                const {
                  ctx,
                  chartArea: { top, bottom },
                } = context.chart;
                const gradientBg = ctx.createLinearGradient(0, top, 0, bottom);
                gradientBg.addColorStop(0, 'rgba(134, 194, 235, .4)');
                gradientBg.addColorStop(1, 'rgba(134, 194, 235, 0)');
                return gradientBg;
              },
              fill: true,
            },
            point: {
              radius: 8,
              hoverRadius: 13,
              borderColor: 'transparent',
              backgroundColor: 'transparent',
              hoverBackgroundColor: 'rgba(134, 194, 235, .9)',
              hoverBorderColor: 'white',
              hoverBorderWidth: 4,
            },
          },
          ...options,
        }}
        data={
          dataInjected || {
            labels: ['', '', '', '', ''],
            datasets: [
              {
                label: 'Aktueller Zeitraum',
                data: [0, 10, 9, 12, 20],
              },
            ],
          }
        }
      />
    </div>
  );
};
